import React, { useMemo, useRef, useCallback } from 'react';
import { Link } from 'react-router-dom';
import ProductImage from './ProductImage';
import { formatPrice } from '../utils/formatPrice';
import '../css/RelatedProducts.css';

const CARD_GAP = 16;
const SCROLL_AMOUNT = 3;

const RelatedProducts = ({ products = [], modelName, brandName, categorySlug }) => {
    const trackRef = useRef(null);

    const modelLabel = [brandName, modelName].filter(Boolean).join(' ');

    const catalogUrl = useMemo(() => {
        const brandSlug = brandName?.toLowerCase().replace(/\s+/g, '-') ?? '';
        const modelSlug = modelName?.toLowerCase().replace(/\s+/g, '-') ?? '';
        const base = categorySlug ? `/zapchastyny/${categorySlug}` : '/zapchastyny';
        if (brandSlug && modelSlug) return `${base}/${brandSlug}/${modelSlug}`;
        if (brandSlug) return `${base}/${brandSlug}`;
        return base;
    }, [brandName, modelName, categorySlug]);

    const structuredData = useMemo(() => {
        if (!products.length) return null;
        return {
            '@context': 'https://schema.org',
            '@type': 'ItemList',
            name: `Інші запчастини${modelLabel ? ` для ${modelLabel}` : ''}`,
            itemListElement: products.slice(0, 10).map((p, i) => ({
                '@type': 'ListItem',
                position: i + 1,
                url: `https://union-auto.com.ua/products/${p.slug || p.id}`,
                name: p.name_ua,
            })),
        };
    }, [products, modelLabel]);

    const scrollTrack = useCallback((direction) => {
        if (!trackRef.current) return;
        const cardWidth =
            trackRef.current.querySelector('.related-card')?.offsetWidth ?? 200;
        trackRef.current.scrollBy({
            left: direction * (cardWidth + CARD_GAP) * SCROLL_AMOUNT,
            behavior: 'smooth',
        });
    }, []);

    if (products.length < 2) return null;

    return (
        <section
            className="related-products-section"
            aria-label={`Інші запчастини${modelLabel ? ` для ${modelLabel}` : ''}`}
        >
            {structuredData && (
                <script
                    type="application/ld+json"
                    dangerouslySetInnerHTML={{ __html: JSON.stringify(structuredData) }}
                />
            )}

            <div className="related-products-header">
                <h2 className="related-products-title">
                    Інші запчастини{modelLabel ? ' для ' : ''}{' '}
                    <Link to={catalogUrl} className="related-model-link">
                        {modelLabel || 'цієї категорії'}
                    </Link>
                </h2>
                <Link to={catalogUrl} className="related-see-all">
                    Всі товари →
                </Link>
            </div>

            <div className="related-carousel-wrapper">
                <button
                    className="related-carousel-btn related-carousel-btn--prev"
                    onClick={() => scrollTrack(-1)}
                    aria-label="Прокрутити назад"
                    type="button"
                >
                    ‹
                </button>

                <ul className="related-carousel-track" ref={trackRef} role="list">
                    {products.map((p) => (
                        <li key={p.id} className="related-card">
                            <Link
                                to={`/products/${p.slug || p.id}`}
                                className="related-card-link"
                                title={p.name_ua}
                            >
                                <div className="related-card-img-wrap">
                                    <ProductImage
                                        src={p.photo}
                                        alt={p.name_ua}
                                        className="related-card-img"
                                        loading="lazy"
                                        decoding="async"
                                    />
                                </div>
                                <div className="related-card-body">
                                    <p className="related-card-name">{p.name_ua}</p>
                                    {p.price && (
                                        <p className="related-card-price">
                                            {formatPrice(p.price)} ₴
                                        </p>
                                    )}
                                </div>
                            </Link>
                        </li>
                    ))}
                </ul>

                <button
                    className="related-carousel-btn related-carousel-btn--next"
                    onClick={() => scrollTrack(1)}
                    aria-label="Прокрутити вперед"
                    type="button"
                >
                    ›
                </button>
            </div>
        </section>
    );
};

export default RelatedProducts;
